import { CheckCircle2, Download, FileQuestion, Lock, Users, XCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { approveWindow, bytes, decryptSegment, unwrapWindowKey } from "@tether/crypto";
import { paths } from "@tether/shared";
import { useSession } from "../App.js";
import { api, apiBytes } from "../lib/api.js";
import { loadGroupKey, openSealed } from "../lib/keys.js";
import { Spinner } from "../lib/Spinner.js";
import { touchVault } from "../lib/recentVault.js";
import { statusBadge, type UnlockRequestView } from "./Unlock.js";

interface Grant {
  vaultId: string;
  vaultPublicKey: string;
  grantExpiresAt: string | null;
  windows: {
    deviceId: string;
    windowId: string;
    epoch: string;
    key: string;
    segments: string[];
    partials: { index: number; value: string }[];
  }[];
}
interface Decrypted {
  name: string;
  streamId: string;
  seq: number;
  size: number;
  url: string;
  mime: string;
}

const MIME: Record<string, string> = { audio: "audio/mp4", video: "video/mp4" };

export function UnlockRequest() {
  const { id = "" } = useParams();
  const { me } = useSession();
  const [r, setR] = useState<UnlockRequestView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [files, setFiles] = useState<Decrypted[]>([]);
  const [denyReason, setDenyReason] = useState("");

  const load = useCallback(async () => {
    try {
      setR((await api<{ request: UnlockRequestView }>(`/v1/unlock-requests/${id}`)).request);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [id]);
  useEffect(() => {
    void load();
    const t = setInterval(() => {
      if (!document.hidden) void load();
    }, 8000);
    return () => clearInterval(t);
  }, [load]);

  const vault = me?.vaults.find((v) => v.id === r?.vaultId);

  async function approve() {
    if (!me || !r || !vault) return;
    // Kid mode: parent's share is on the vault; guardian/circle: on the confidant record.
    const sealed =
      vault.mode === "kid" ? vault.sealedShareForOwner : (vault.confidant?.sealedShare ?? null);
    const shareIndex = vault.mode === "kid" ? 1 : (vault.confidant?.shareIndex ?? 0);
    if (!sealed || !shareIndex) return;
    setBusy(true);
    setErr(null);
    try {
      const kp = loadGroupKey(me.user.id);
      if (!kp)
        throw new Error(
          "Your decryption key is missing in this browser. Restore it from your backup in Settings.",
        );
      const shareBytes = openSealed(kp, sealed, `share:${vault.publicKey}:${shareIndex}`);
      const share = { index: shareIndex, value: shareBytes };
      const partials: string[] = [];
      for (let i = 0; i < r.windows.length; i++) {
        setProgress(`Computing approval ${i + 1} of ${r.windows.length}…`);
        const wrapped = await apiBytes(
          `/v1/vaults/${r.vaultId}/object?key=${encodeURIComponent(r.windows[i]!.key)}`,
        );
        partials.push(bytes.toBase64Url(approveWindow(share, wrapped).value));
      }
      shareBytes.fill(0);
      await api(`/v1/unlock-requests/${id}/approve`, { body: { partials } });
      touchVault(me.user.id, r.vaultId);
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }
  async function deny() {
    setBusy(true);
    setErr(null);
    try {
      await api(`/v1/unlock-requests/${id}/deny`, {
        body: denyReason ? { reason: denyReason } : {},
      });
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function revoke() {
    if (!confirm("Withdraw this request? Any grant is destroyed.")) return;
    await api(`/v1/unlock-requests/${id}/revoke`, { body: {} });
    await load();
  }
  async function decrypt() {
    if (!r) return;
    setBusy(true);
    setErr(null);
    setFiles([]);
    try {
      const g = await api<Grant>(`/v1/unlock-requests/${id}/grant`);
      const vpk = bytes.fromBase64Url(g.vaultPublicKey);
      const out: Decrypted[] = [];
      let n = 0;
      const total = g.windows.reduce((a, w) => a + w.segments.length, 0);
      for (const w of g.windows) {
        const wrapped = await apiBytes(
          `/v1/vaults/${g.vaultId}/object?key=${encodeURIComponent(w.key)}`,
        );
        const ref = paths.windowRef(w.windowId, w.epoch);
        const partials = w.partials.map((p) => ({
          index: p.index,
          value: bytes.fromBase64Url(p.value),
        }));
        const wk = unwrapWindowKey(vpk, g.vaultId, ref, wrapped, partials);
        for (const segKey of w.segments) {
          n++;
          setProgress(`Decrypting segment ${n} of ${total}…`);
          const m = /\/([a-z0-9]+)-(\d+)\.seg$/.exec(segKey);
          if (!m) continue;
          const streamId = m[1]!;
          const seq = Number(m[2]);
          const ct = await apiBytes(
            `/v1/vaults/${g.vaultId}/object?key=${encodeURIComponent(segKey)}`,
          );
          const pt = decryptSegment(wk, g.vaultId, ref, streamId, seq, ct);
          // Android's MediaRecorder stamps AAC output with the `3gp4` major brand even when
          // asked for MPEG-4 output. Chrome's <audio> element rejects that brand/MIME mismatch.
          // Rewrite the 4-byte major brand to `mp42` (bytes 8-12 of the ftyp box); the bit-for-bit
          // audio content is identical.
          if (
            streamId === "audio" &&
            pt.length >= 12 &&
            pt[4] === 0x66 &&
            pt[5] === 0x74 &&
            pt[6] === 0x79 &&
            pt[7] === 0x70 && // "ftyp"
            pt[8] === 0x33 &&
            pt[9] === 0x67 &&
            pt[10] === 0x70 &&
            pt[11] === 0x34 // "3gp4"
          ) {
            pt[8] = 0x6d;
            pt[9] = 0x70;
            pt[10] = 0x34;
            pt[11] = 0x32; // "mp42"
          }
          const mime = MIME[streamId] ?? "application/octet-stream";
          const url = URL.createObjectURL(new Blob([pt as BlobPart], { type: mime }));
          out.push({
            name: `${w.deviceId}-${w.windowId}-${w.epoch}-${streamId}-${seq}`,
            streamId,
            seq,
            size: pt.length,
            url,
            mime,
          });
        }
        wk.fill(0);
      }
      out.sort((a, b) => a.streamId.localeCompare(b.streamId) || a.seq - b.seq);
      setFiles(out);
      if (me) touchVault(me.user.id, r.vaultId);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  if (!r) return err ? <p className="error">{err}</p> : <Spinner label="Loading…" />;
  const canDecrypt = r.status === "approved" && (r.me.isRequester || vault?.role === "owner");
  return (
    <div>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h2 style={{ margin: "0 0 12px" }}>
          Unlock request · {r.vaultName} {statusBadge(r.status)}
        </h2>
        {(r.me.isRequester || vault?.role === "owner") &&
          (r.status === "pending" || r.status === "approved") && (
            <button
              className="danger"
              onClick={revoke}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <XCircle size={14} />
              Withdraw
            </button>
          )}
      </div>
      {err && <p className="error">{err}</p>}
      <div className="grid">
        <div className="card">
          <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FileQuestion size={18} className="icon" />
            What is being asked
          </h2>
          <p>
            <strong>{r.requester.name}</strong> asks to open{" "}
            <strong>{new Date(r.fromMs).toLocaleString()}</strong> to{" "}
            <strong>{new Date(r.toMs).toLocaleString()}</strong> ({r.windowCount} window
            {r.windowCount === 1 ? "" : "s"}).
          </p>
          <p>
            Reason: <strong>{r.reason}</strong>
            {r.details && <span> · {r.details}</span>}
          </p>
          {r.policeReport && (
            <p className="muted">
              Police report {r.policeReport.number} · {r.policeReport.agency} ·{" "}
              {r.policeReport.contact}
            </p>
          )}
          <p className="muted">
            Requested {new Date(r.createdAt).toLocaleString()} · expires{" "}
            {new Date(r.expiresAt).toLocaleString()}
            {r.grantExpiresAt && (
              <> · grant valid until {new Date(r.grantExpiresAt).toLocaleString()}</>
            )}
          </p>
        </div>
        <div className="card">
          <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Users size={18} className="icon" />
            Approvals {r.approvals.length} / {r.needed}
          </h2>
          <ul className="list-plain">
            {r.approvals.map((a) => (
              <li key={a.confidantId}>
                <span className="badge ok">approved</span> {a.name} ·{" "}
                {new Date(a.at).toLocaleString()}
              </li>
            ))}
            {r.denials.map((d) => (
              <li key={d.confidantId}>
                <span className="badge bad">denied</span> {d.name} ·{" "}
                {new Date(d.at).toLocaleString()}
                {d.reason && <span className="muted"> · {d.reason}</span>}
              </li>
            ))}
          </ul>
          {r.me.canApprove && (
            <div style={{ marginTop: 12 }}>
              <p className="muted">
                Approving computes your part of the key for exactly these {r.windowCount} window
                {r.windowCount === 1 ? "" : "s"} on this device. Your share never leaves it, and
                this approval cannot be reused for any other time range.
              </p>
              {progress && <p>{progress}</p>}
              <div className="row">
                <button
                  className="primary"
                  onClick={approve}
                  disabled={busy}
                  style={{ display: "flex", alignItems: "center", gap: 6 }}
                >
                  <CheckCircle2 size={15} />
                  Approve
                </button>
                <input
                  style={{ maxWidth: 240 }}
                  placeholder="Reason (optional)"
                  value={denyReason}
                  onChange={(e) => setDenyReason(e.target.value)}
                />
                <button
                  className="danger"
                  onClick={deny}
                  disabled={busy}
                  style={{ display: "flex", alignItems: "center", gap: 6 }}
                >
                  <XCircle size={15} />
                  Deny
                </button>
              </div>
            </div>
          )}
          {r.me.approved && <p className="muted">You approved this request.</p>}
          {r.me.denied && <p className="muted">You denied this request.</p>}
        </div>
      </div>

      {canDecrypt && (
        <div className="card">
          <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Lock size={18} className="icon" />
            Decrypt
          </h2>
          <p className="muted">
            The quorum has approved. Decryption happens in this browser: the parts are combined
            here, and the gateway never sees a key. Downloads are the raw recording segments.
          </p>
          {progress && <p>{progress}</p>}
          <button
            className="primary"
            onClick={decrypt}
            disabled={busy}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <Lock size={15} />
            Decrypt the approved recordings
          </button>
          {files.length > 0 && (
            <table style={{ marginTop: 12 }}>
              <thead>
                <tr>
                  <th>Segment</th>
                  <th>Size</th>
                  <th>Play</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {files.map((f) => (
                  <tr key={f.name}>
                    <td>{f.name}</td>
                    <td>{(f.size / 1024).toFixed(1)} KB</td>
                    <td>
                      {f.streamId === "audio" && <audio controls src={f.url} preload="none" />}
                      {f.streamId === "video" && (
                        <video controls src={f.url} width={220} preload="none" />
                      )}
                    </td>
                    <td>
                      <a
                        href={f.url}
                        download={`${f.name}.${f.streamId === "audio" ? "m4a" : f.streamId === "video" ? "mp4" : "bin"}`}
                        style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                      >
                        <Download size={14} />
                        Download
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
